/**
 * Public help shell — no auth; branded header and footer.
 * @viewport both
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Link,
  Typography,
} from '@mui/material';
import HFRLogo from '@/components/common/HFRLogo';
import { FONT_SIZE, SPACING, TOUCH } from '@/core/platform/platformStandards';
import {
  AUTH_HELP_PATHS,
  AUTH_HELP_SUPPORT_EMAIL,
} from '@/features/help/helpContent';

const HelpPublicLayout = ({ title, subtitle, children }) => (
  <Box
    sx={{
      minHeight: '100dvh',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.default',
    }}
  >
    <Box
      component="header"
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        py: { xs: 1.5, md: 2 },
      }}
    >
      <Container maxWidth="md">
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <RouterLink to={AUTH_HELP_PATHS.hub} style={{ textDecoration: 'none' }}>
            <HFRLogo height={48} />
          </RouterLink>
          <Button
            component={RouterLink}
            to="/register"
            variant="contained"
            size="small"
            sx={{ minHeight: TOUCH.minSize }}
          >
            Create account
          </Button>
        </Box>
      </Container>
    </Box>

    <Container maxWidth="md" sx={{ flex: 1, py: { xs: 2, md: 3 }, px: { xs: 2, md: 3 } }}>
      {title ? (
        <Box sx={{ mb: SPACING.xxl / 8 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="body1" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      ) : null}
      {children}
    </Container>

    <Box
      component="footer"
      sx={{
        borderTop: 1,
        borderColor: 'divider',
        py: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Container maxWidth="md">
        <Typography variant="body2" color="text.secondary" align="center" sx={{ fontSize: FONT_SIZE.xs }}>
          <Link component={RouterLink} to={AUTH_HELP_PATHS.hub} color="inherit">
            Help home
          </Link>
          {' · '}
          <Link href={`mailto:${AUTH_HELP_SUPPORT_EMAIL}`} color="inherit">
            {AUTH_HELP_SUPPORT_EMAIL}
          </Link>
        </Typography>
      </Container>
    </Box>
  </Box>
);

HelpPublicLayout.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export default HelpPublicLayout;
